import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

/**
 * Tiroir « Arbre technologique » — port fidèle du prototype (`network.js`
 * drawerTechnos) : catégories, cartes d'état (Acquis / En cours / Disponible /
 * Verrouillé), liste d'effets, prérequis, coût argent + durée. Branché sur le
 * VRAI endpoint `/tech-tree` (mêmes données que TechTreeView).
 */

interface TechNode {
  id: string;
  nom: string;
  description: string;
  categorie: 'personnel' | 'vehicules' | 'operations' | 'infrastructure';
  cout_argent: number;
  cout_medailles: number;
  duree_recherche_secondes: number;
  prerequis: string[];
  effets: string[];
  icone: string;
}
interface PlayerNodeState {
  node_id: string;
  statut: 'en_recherche' | 'complete';
  started_at: string;
  completed_at: string | null;
}
interface TechTreeState {
  nodes: TechNode[];
  player: PlayerNodeState[];
}

const CATS: { key: TechNode['categorie']; nom: string; color: string }[] = [
  { key: 'personnel', nom: 'Personnel', color: '#7dd3fc' },
  { key: 'vehicules', nom: 'Véhicules', color: '#fcd34d' },
  { key: 'operations', nom: 'Opérations', color: '#6ee7b7' },
  { key: 'infrastructure', nom: 'Infrastructure', color: '#c4b5fd' },
];

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const euros = (n: number) => (n > 0 ? `${n.toLocaleString('fr-FR')} €` : 'Gratuit');
const dureeCourte = (s: number) => {
  if (s <= 1) return 'Immédiat';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
};

const TechnosDrawer: React.FC = () => {
  const [state, setState] = useState<TechTreeState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);
  const base = `${getApiBaseUrl(true)}/tech-tree`;

  const fetchState = useCallback(async () => {
    try {
      const res = await fetch(base, { credentials: 'include', headers: authHeaders() });
      if (!res.ok) {
        setError(
          res.status === 404
            ? "Service de l'arbre technologique indisponible (serveur à mettre à jour)."
            : `Erreur serveur (${res.status}).`,
        );
        return;
      }
      const data = await res.json().catch(() => null);
      if (data?.success) {
        setState(data.data);
        setError(null);
      } else setError(data?.error || 'Réponse serveur invalide.');
    } catch (e: any) {
      setError(e?.message ? `Serveur injoignable : ${e.message}` : 'Serveur injoignable.');
    }
  }, [base]);

  useEffect(() => {
    fetchState();
    const refresh = setInterval(fetchState, 16_000);
    const tick = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      clearInterval(refresh);
      clearInterval(tick);
    };
  }, [fetchState]);

  const action = async (nodeId: string, kind: 'start' | 'cancel') => {
    setBusy(true);
    try {
      const res = await fetch(`${base}/${nodeId}/${kind}`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => null);
      if (data?.success) setState(data.data);
      else setError(data?.error || 'Action refusée.');
    } catch (e: any) {
      setError(e?.message || 'Action impossible.');
    } finally {
      setBusy(false);
    }
  };

  const playerOf = useCallback(
    (id: string) => state?.player.find((p) => p.node_id === id) ?? null,
    [state],
  );
  const isComplete = (id: string) => playerOf(id)?.statut === 'complete';
  const isResearching = (id: string) => playerOf(id)?.statut === 'en_recherche';
  const anyWip = useMemo(() => state?.player.some((p) => p.statut === 'en_recherche') ?? false, [state]);

  const remaining = (n: TechNode) => {
    const p = playerOf(n.id);
    if (!p || p.statut !== 'en_recherche') return 0;
    const elapsed = (Date.now() - new Date(p.started_at).getTime()) / 1000;
    return Math.max(0, Math.ceil(n.duree_recherche_secondes - elapsed));
  };
  const progress = (n: TechNode) => {
    const p = playerOf(n.id);
    if (!p || p.statut !== 'en_recherche') return 0;
    const elapsed = (Date.now() - new Date(p.started_at).getTime()) / 1000;
    return Math.min(100, Math.round((elapsed / n.duree_recherche_secondes) * 100));
  };
  const countdown = (s: number) => {
    if (s <= 0) return 'Terminé';
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const sec = s % 60;
    if (m < 60) return `${m}:${String(sec).padStart(2, '0')}`;
    const h = Math.floor(m / 60);
    return `${h}h ${String(m % 60).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const etatOf = (n: TechNode): 'acquis' | 'encours' | 'dispo' | 'verrou' => {
    if (isComplete(n.id)) return 'acquis';
    if (isResearching(n.id)) return 'encours';
    return n.prerequis.every((p) => isComplete(p)) ? 'dispo' : 'verrou';
  };

  if (!state) {
    return (
      <div className="empty" style={{ margin: 16 }}>
        {error || "Chargement de l'arbre technologique…"}
      </div>
    );
  }

  const wipNode = state.nodes.find((n) => isResearching(n.id)) || null;
  const nameOf = (id: string) => state.nodes.find((n) => n.id === id)?.nom || id;

  const phaseClass: Record<string, string> = { acquis: 'done', encours: 'site', dispo: 'transit', verrou: 'wait' };
  const phaseLabel: Record<string, string> = { acquis: 'Acquis', encours: 'En cours', dispo: 'Disponible', verrou: 'Verrouillé' };

  return (
    <>
      <p className="dintro">
        Arbre relevé du serveur : catégories, prérequis réels, une recherche à la fois.
        <b> Le coût est en argent</b> ; les médailles ne servent qu'à un éventuel démarrage prioritaire,
        jamais à débloquer.
      </p>

      {error ? <p className="bwarn" style={{ marginBottom: 10 }}>⚠ {error}</p> : null}

      {wipNode ? (
        <div className="block block--alert">
          <div className="block__h">Recherche en cours</div>
          <div className="block__b">
            <div className="need">
              <span className="need__t">{wipNode.nom}</span>
              <span className="need__bar"><i style={{ width: `${progress(wipNode)}%` }} /></span>
              <span className="need__c">{countdown(remaining(wipNode))}</span>
              <button className="btn btn--sm btn--ghost" type="button" disabled={busy} onClick={() => action(wipNode.id, 'cancel')}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {CATS.map((cat) => {
        const nodes = state.nodes.filter((n) => n.categorie === cat.key);
        if (!nodes.length) return null;
        return (
          <div className="deptblock" key={cat.key}>
            <p className="deptblock__h" style={{ color: cat.color }}>{cat.nom}</p>
            <div className="grid-cards">
              {nodes.map((n) => {
                const e = etatOf(n);
                const manquants = n.prerequis.filter((p) => !isComplete(p)).map(nameOf);
                return (
                  <div className={`scard ${e === 'verrou' ? 'is-dead' : ''}`} key={n.id}>
                    <div className="scard__h">
                      <span className="scard__ico">{n.icone}</span>
                      <div className="scard__id">
                        <p className="scard__nom">{n.nom}</p>
                        <p className="scard__sub">{euros(n.cout_argent)} · {dureeCourte(n.duree_recherche_secondes)}</p>
                      </div>
                      <span className={`phase phase--${phaseClass[e]}`}>{phaseLabel[e]}</span>
                    </div>
                    {n.effets.length ? (
                      <ul className="teff">
                        {n.effets.map((x, i) => (
                          <li key={i}>{x}</li>
                        ))}
                      </ul>
                    ) : null}
                    {e === 'verrou' ? <p className="bwarn">Prérequis : {manquants.join(', ')}</p> : null}
                    {e === 'encours' ? (
                      <button className="btn btn--sm btn--ghost btn--block" style={{ marginTop: 7 }} type="button" disabled={busy} onClick={() => action(n.id, 'cancel')}>
                        Annuler la recherche
                      </button>
                    ) : (
                      <button
                        className={`btn btn--sm ${e === 'dispo' ? 'btn--primary' : 'btn--ghost'} btn--block`}
                        style={{ marginTop: 7 }}
                        type="button"
                        disabled={e !== 'dispo' || anyWip || busy}
                        onClick={() => e === 'dispo' && action(n.id, 'start')}
                      >
                        {e === 'acquis'
                          ? 'Déjà acquis'
                          : e === 'verrou'
                            ? 'Prérequis manquant'
                            : anyWip
                              ? 'Une recherche en cours'
                              : `Lancer · ${euros(n.cout_argent)}`}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default TechnosDrawer;
